import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';

// Mapeamento de nomes amigáveis para nomes do banco
const techniqueNameMap = {
  'Closed Guard': 'Closed Guard',
  'Open Guard': 'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)',
  'Half Guard': 'Half Guard (e.g., Deep Half, Z-Guard, Knee Shield)',
  'Butterfly Guard': 'Butterfly Guard',
  'Worm Guard': 'Worm Guard',
  'Reverse De La Riva Guard': 'Reverse De La Riva Guard (RDLR)',
  '50/50 Guard': '50/50 Guard',
  'Sitting Guard': 'Sitting Guard',
  'No-Gi Specific Guards': 'No-Gi Specific Guards (e.g., Leg Pummeling, Ashi Garami)',
  'Pressure Passing': 'Pressure Passing (e.g., Knee Slice, Over Under, Stack Pass)',
  'Torque/Leg Drag Passing': 'Torque/Leg Drag Passing',
  'Leg Weave Passing': 'Leg Weave Passing',
  'Floating/Smash Passing': 'Floating/Smash Passing',
  'Movement-Based Passing': 'Movement-Based Passing (e.g., Toreando, Matador)',
  'Folding Passes': 'Folding Passes (e.g., Double Under)',
  'No-Gi Specific Passes': 'No-Gi Specific Passes (e.g., Head & Arm Pass, Darce Pass from Guard)',
  'Armlocks': 'Armlocks (e.g., Armbar, Kimura, Americana, Omoplata)',
  'Chokes': 'Chokes (e.g., Rear Naked Choke, Guillotine, Triangle, Cross Collar Choke, Darce, Anaconda, Bow and Arrow)',
  'Leg Locks': 'Leg Locks (e.g., Heel Hook, Kneebar, Ankle Lock, Calf Slicer)',
  'Shoulder Locks': 'Shoulder Locks (e.g., Wrist Lock, Omoplata, Americana)',
  'Spine/Neck Cranks': 'Spine/Neck Cranks (e.g., Twister)',
  'No-Gi Specific Submissions': 'No-Gi Specific Submissions (e.g., Head & Arm Choke, Ezekiel from Mount)',
  'Guard Sweeps': 'Guard Sweeps (e.g., Scissor Sweep, Hip Bump Sweep, Flower Sweep, Hook Sweeps)',
  'Half Guard Sweeps': 'Half Guard Sweeps (e.g., Old School Sweep, Waiter Sweep)',
  'Butterfly Sweeps': 'Butterfly Sweeps',
  'De La Riva Sweeps': 'De La Riva Sweeps',
  'Takedowns from Standing': 'Takedowns from Standing (e.g., Double Leg, Single Leg, Judo Throws, Body Lock)',
  'Counter Takedowns': 'Counter Takedowns',
  'Pulling Guard': 'Pulling Guard',
  'Mount Escapes': 'Mount Escapes (e.g., Upa, Elbow-Knee Escape)',
  'Back Escapes': 'Back Escapes (e.g., Bridge & Roll, Hip Escape)',
  'Side Control Escapes': 'Side Control Escapes (e.g., Hip Escape to Guard, Underhook Escapes)',
  'North South Escapes': 'North South Escapes',
  'Submission Escapes': 'Submission Escapes (e.g., Armbar Escape, Triangle Escape)',
  'Turtle Escapes': 'Turtle Escapes',
  'Half Guard Escapes': 'Half Guard Escapes',
};

const techniqueCategories = [
  {
    name: 'Guard Types',
    techniques: [
      'Closed Guard',
      'Open Guard',
      'Half Guard',
      'Butterfly Guard',
      'Worm Guard',
      'Reverse De La Riva Guard',
      '50/50 Guard',
      'Sitting Guard',
      'No-Gi Specific Guards',
    ],
  },
  {
    name: 'Guard Passing',
    techniques: [
      'Pressure Passing',
      'Torque/Leg Drag Passing',
      'Leg Weave Passing',
      'Floating/Smash Passing',
      'Movement-Based Passing',
      'Folding Passes',
      'No-Gi Specific Passes',
    ],
  },
  {
    name: 'Submissions',
    techniques: [
      'Armlocks',
      'Chokes',
      'Leg Locks',
      'Shoulder Locks',
      'Spine/Neck Cranks',
      'No-Gi Specific Submissions',
    ],
  },
  {
    name: 'Sweeps & Takedowns',
    techniques: [
      'Guard Sweeps',
      'Half Guard Sweeps',
      'Butterfly Sweeps',
      'De La Riva Sweeps',
      'Takedowns from Standing',
      'Counter Takedowns',
      'Pulling Guard',
    ],
  },
  {
    name: 'Escapes & Reversals',
    techniques: [
      'Mount Escapes',
      'Back Escapes',
      'Side Control Escapes',
      'North South Escapes',
      'Submission Escapes',
      'Turtle Escapes',
      'Half Guard Escapes',
    ],
  },
];

export interface EditFavoriteTechniquesModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Record<string, string[]>) => Promise<void>;
  initialData: Record<string, string[]>;
}

export const EditFavoriteTechniquesModal: React.FC<EditFavoriteTechniquesModalProps> = ({ open, onClose, onSave, initialData }) => {
  const { t } = useI18n();
  const [selected, setSelected] = useState<Record<string, string[]>>(initialData || {});
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const [categoryIndex, setCategoryIndex] = useState(0);

  function getFriendlyName(dbName: string): string | undefined {
    // Retorna a chave do techniqueNameMap cujo valor é igual ao dbName
    return Object.keys(techniqueNameMap).find(key => techniqueNameMap[key] === dbName);
  }

  useEffect(() => {
    // Mapear os nomes do banco para os nomes amigáveis
    const mapped: Record<string, string[]> = {};
    for (const cat of techniqueCategories) {
      const saved = initialData[cat.name] || [];
      mapped[cat.name] = saved.map(dbName => {
        // Se já for amigável, mantém; senão, tenta mapear
        if (cat.techniques.includes(dbName)) return dbName;
        const friendly = getFriendlyName(dbName);
        return friendly && cat.techniques.includes(friendly) ? friendly : dbName;
      });
    }
    setSelected(mapped);
    setCategoryIndex(0);
  }, [initialData, open]);

  const currentCategory = techniqueCategories[categoryIndex];
  const localSelected = selected[currentCategory.name] || [];
  
  const getCategoryName = (name: string) => {
    switch (name) {
      case 'Guard Types': return t('profile.technique_categories.guard_types');
      case 'Guard Passing': return t('profile.technique_categories.guard_passing');
      case 'Submissions': return t('profile.technique_categories.submissions');
      case 'Sweeps & Takedowns': return t('profile.technique_categories.sweeps_takedowns');
      case 'Escapes & Reversals': return t('profile.technique_categories.escapes_reversals');
      default: return name;
    }
  };

  const getTechniqueTranslation = (techniqueName: string) => {
    // Mapeamento de nomes do banco para chaves de tradução (reutilizando do TrainingViewModal)
    const techniqueMapping: { [key: string]: string } = {
      // Guard Types
      'Closed Guard': 'closed_guard',
      'Open Guard': 'open_guard',
      'Open Guard (e.g., De La Riva, Spider, Lasso, X-Guard, Half Guard)': 'open_guard',
      'Half Guard': 'half_guard',
      'Butterfly Guard': 'butterfly_guard',
      'Worm Guard': 'worm_guard',
      'Reverse De La Riva Guard': 'reverse_de_la_riva_guard',
      '50/50 Guard': '50_50_guard',
      'Sitting Guard': 'sitting_guard',
      'No-Gi Specific Guards': 'no_gi_specific_guards',
      
      // Guard Passing
      'Pressure Passing': 'pressure_passing',
      'Torque/Leg Drag Passing': 'torque_leg_drag_passing',
      'Leg Weave Passing': 'leg_weave_passing',
      'Floating/Smash Passing': 'floating_smash_passing',
      'Movement-Based Passing': 'movement_based_passing',
      'Folding Passes': 'folding_passes',
      'No-Gi Specific Passes': 'no_gi_specific_passes',
      
      // Submissions
      'Armlocks': 'armlocks',
      'Chokes': 'chokes',
      'Leg Locks': 'leg_locks',
      'Shoulder Locks': 'shoulder_locks',
      'Spine/Neck Cranks': 'spine_neck_cranks',
      'No-Gi Specific Submissions': 'no_gi_specific_submissions',
      
      // Sweeps & Takedowns
      'Guard Sweeps': 'guard_sweeps',
      'Half Guard Sweeps': 'half_guard_sweeps',
      'Butterfly Sweeps': 'butterfly_sweeps',
      'De La Riva Sweeps': 'de_la_riva_sweeps',
      'Takedowns from Standing': 'takedowns_from_standing',
      'Counter Takedowns': 'counter_takedowns',
      'Pulling Guard': 'pulling_guard',
      
      // Escapes & Reversals
      'Mount Escapes': 'mount_escapes',
      'Back Escapes': 'back_escapes',
      'Side Control Escapes': 'side_control_escapes',
      'North South Escapes': 'north_south_escapes',
      'Submission Escapes': 'submission_escapes',
      'Turtle Escapes': 'turtle_escapes',
      'Half Guard Escapes': 'half_guard_escapes'
    };

    // Try to find the mapped key
    const mappedKey = techniqueMapping[techniqueName];
    if (mappedKey) {
      return t(`calendar.techniques.${mappedKey}`) || techniqueName;
    }

    // Fallback: try to convert the name directly (for any new techniques)
    const techniqueKey = techniqueName.toLowerCase().replace(/\s+/g, '_');
    return t(`calendar.techniques.${techniqueKey}`) || techniqueName;
  };

  const handleSelect = (value: string) => {
    setSelected((prev) => {
      const prevCat = prev[currentCategory.name] || [];
      if (prevCat.includes(value)) {
        return { ...prev, [currentCategory.name]: prevCat.filter((t) => t !== value) };
      } else if (prevCat.length < 3) {
        return { ...prev, [currentCategory.name]: [...prevCat, value] };
      } else {
        return { ...prev, [currentCategory.name]: [...prevCat.slice(1), value] };
      }
    });
  };

  const handleNext = () => {
    if (categoryIndex < techniqueCategories.length - 1) {
      setCategoryIndex(categoryIndex + 1);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    if (categoryIndex > 0) {
      setCategoryIndex(categoryIndex - 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(selected);
    setSaving(false);
  };

  const isLastCategory = categoryIndex === techniqueCategories.length - 1;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-2xl bg-white rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-black font-sans">{t('profile.edit_favorite_techniques')}</DialogTitle>
          <DialogDescription className="text-gray-600 text-sm">
            {t('profile.edit_favorite_techniques_description')}
          </DialogDescription>
        </DialogHeader>
        {/* Barra de progresso e texto de passo */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-[#b0b0b0] mb-2">
            <span>{t('profile.category_progress', { current: categoryIndex + 1, total: techniqueCategories.length })}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-[#D72E16] h-2 rounded-full transition-all duration-300"
              style={{ width: `${((categoryIndex + 1) / techniqueCategories.length) * 100}%` }}
            ></div>
          </div>
        </div>
        {/* Categoria atual */}
        <div className="space-y-6">
          <div className="mb-2">
            <div className="font-medium mb-1 text-black font-sans">{getCategoryName(currentCategory.name)}</div>
            <div className="text-xs text-[#b0b0b0] mb-2 font-sans">{t('profile.techniques_selected', { count: localSelected.length })}</div>
                          <div className="flex flex-wrap gap-2">
                {currentCategory.techniques.map((tech) => {
                  const isSelected = localSelected.includes(tech);
                  const isDisabled = !isSelected && localSelected.length >= 3;
                  return (
                    <button
                      key={tech}
                      type="button"
                      onClick={() => handleSelect(tech)}
                      className={
                        'rounded px-3 py-1 text-sm font-medium transition-colors duration-200 border font-sans ' +
                        (isSelected
                          ? 'bg-[#D72E16] text-white border-[#D72E16]'
                          : isDisabled
                          ? 'border-black text-[#b0b0b0] bg-white cursor-not-allowed'
                          : 'border-black text-black bg-white hover:bg-[#D72E16] hover:text-white cursor-pointer') +
                        ' focus:outline-none focus:ring-0'
                      }
                      tabIndex={0}
                      aria-pressed={isSelected}
                      disabled={false}
                    >
                      {getTechniqueTranslation(tech)}
                    </button>
                  );
                })}
              </div>
            {localSelected.length > 0 && localSelected.length < 3 && (
              <div className="text-xs text-orange-600 mt-2 font-sans">
                {t('profile.tip_select_techniques')}
              </div>
            )}
          </div>
        </div>
        {/* Navegação */}
        <div className="flex gap-2 mt-8">
          <Button
            onClick={handleBack}
            disabled={categoryIndex === 0 || saving}
            variant="outline"
            className="border-black text-black bg-white font-sans rounded-full flex-1"
          >
            {t('profile.back')}
          </Button>
          <Button
            onClick={handleNext}
            disabled={saving}
            className="bg-[#D72E16] text-white font-sans rounded-full flex-1"
          >
            {isLastCategory ? (saving ? t('profile.saving') : t('common.save')) : t('profile.next')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 